// Health Intelligence Service - Wearables, Remote Monitoring, Health Scores, Population Health
import api from './api';

export const healthIntelligenceService = {
  // ==================== WEARABLES ====================
  async getWearableDevices(patientId) {
    try {
      const response = await api.get(`/api/v1/health-intelligence/wearables/${patientId}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to fetch wearable devices' };
    }
  },

  async getWearableMetrics(patientId, deviceId = null) {
    try {
      const params = deviceId ? `?device_id=${deviceId}` : '';
      const response = await api.get(`/api/v1/health-intelligence/wearables/${patientId}/metrics${params}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to fetch wearable metrics' };
    }
  },

  async connectWearableDevice(patientId, deviceData) {
    try {
      const response = await api.post(`/api/v1/health-intelligence/wearables/${patientId}/connect`, deviceData);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to connect device' };
    }
  },

  async disconnectWearableDevice(patientId, deviceId) {
    try {
      await api.delete(`/api/v1/health-intelligence/wearables/${patientId}/disconnect/${deviceId}`);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to disconnect device' };
    }
  },

  // ==================== REMOTE MONITORING ====================
  async getMonitoredPatients(providerId = null) {
    try {
      const params = providerId ? `?provider_id=${providerId}` : '';
      const response = await api.get(`/api/v1/health-intelligence/remote-monitoring/patients${params}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to fetch monitored patients' };
    }
  },

  async getPatientMonitoringData(patientId) {
    try {
      const response = await api.get(`/api/v1/health-intelligence/remote-monitoring/${patientId}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to fetch monitoring data' };
    }
  },

  async getAlertRules() {
    try {
      const response = await api.get('/api/v1/health-intelligence/remote-monitoring/alert-rules');
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to fetch alert rules' };
    }
  },

  async updateAlertRule(ruleId, ruleData) {
    try {
      const response = await api.put(`/api/v1/health-intelligence/remote-monitoring/alert-rules/${ruleId}`, ruleData);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to update alert rule' };
    }
  },

  // ==================== HEALTH SCORES ====================
  async getHealthScores(patientId) {
    try {
      const response = await api.get(`/api/v1/health-intelligence/health-scores/${patientId}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to fetch health scores' };
    }
  },

  async getHealthScoreHistory(patientId, months = 6) {
    try {
      const response = await api.get(`/api/v1/health-intelligence/health-scores/${patientId}/history?months=${months}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to fetch health score history' };
    }
  },

  async getRiskAssessment(patientId) {
    try {
      const response = await api.get(`/api/v1/health-intelligence/health-scores/${patientId}/risk-assessment`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to fetch risk assessment' };
    }
  },

  // ==================== POPULATION HEALTH ====================
  async getPopulationSummary() {
    try {
      const response = await api.get('/api/v1/health-intelligence/population-health/summary');
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to fetch population summary' };
    }
  },

  async getPopulationDemographics() {
    try {
      const response = await api.get('/api/v1/health-intelligence/population-health/demographics');
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to fetch demographics' };
    }
  },

  async getChronicConditions() {
    try {
      const response = await api.get('/api/v1/health-intelligence/population-health/chronic-conditions');
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to fetch chronic conditions' };
    }
  },

  async getRiskStratification() {
    try {
      const response = await api.get('/api/v1/health-intelligence/population-health/risk-stratification');
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to fetch risk stratification' };
    }
  },

  async getCareGaps() {
    try {
      const response = await api.get('/api/v1/health-intelligence/population-health/care-gaps');
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to fetch care gaps' };
    }
  },

  async getQualityMetrics() {
    try {
      const response = await api.get('/api/v1/health-intelligence/population-health/quality-metrics');
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to fetch quality metrics' };
    }
  },

  // ==================== RESEARCH CONSENT ====================
  async getResearchStudies() {
    try {
      const response = await api.get('/api/v1/health-intelligence/research/studies');
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to fetch research studies' };
    }
  },

  async getPatientConsents(patientId) {
    try {
      const response = await api.get(`/api/v1/health-intelligence/research/consents/${patientId}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to fetch consents' };
    }
  },

  async updateDataSharingPreferences(patientId, preferences) {
    try {
      const response = await api.put(`/api/v1/health-intelligence/research/preferences/${patientId}`, preferences);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to update preferences' };
    }
  },
};

export default healthIntelligenceService;
